import React, { useEffect, useState } from 'react';
import { twMerge } from 'utilities/src/twMerge';

import { IArticleIndex, createArticleIndex, getArticleId, useSyntaxHighlighter } from 'utils/postBodyUtils';
import iframeSanitizer from 'utilities/src/iframeSanitizer';
import Chip from 'ui/src/components/Chip/';
import extractText from 'utilities/src/extractText';
import type { SinglePostProps } from 'pages/[slug]';
import style from 'react-syntax-highlighter/dist/cjs/styles/prism/prism';
import InputDropdown from 'ui/src/components/Form/Input/InputDropdown';
import DOMPurify from 'isomorphic-dompurify';
import ShareComponent from 'ui/src/components/ShareComponent';
import styles from './PostBody.module.scss';
import PostFooterAuthor from './author/PostFooterAuthor';
import PostAuthor from './author/PostAuthor';
import PrevNextArticles from './PrevNextArticles';
import Image from './Image';

export type PostBodyProps = Partial<SinglePostProps> & { url: string };

DOMPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName === 'h2') {
    node.setAttribute('id', getArticleId(node.textContent ?? ''));
  }
});

function PostBody({
  post,
  hideAuthor,
  hideSharing,
  url,
  previousArticle,
  nextArticle,
  hasArticlesContentsLinks,
}: PostBodyProps) {
  const [articleIndex, setArticleIndex] = useState(() => {
    const initialIndex: IArticleIndex[] = [];
    return initialIndex;
  });
  const [articleContents, setArticleContents] = useState(post?.content ?? '');

  useEffect(() => {
    setArticleIndex(createArticleIndex());
    setArticleContents(iframeSanitizer(post?.content ?? ''));
  }, [post?.content]);

  const { SyntaxHighlighter, codeSections, htmlSections, loaded } = useSyntaxHighlighter({
    formattedArticleContents: articleContents,
  });

  const scrollToSection = (id: string) => {
    const target = document.getElementById(id);
    if (target) {
      target.style.scrollMarginTop = '110px';
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="2xl:w-[60.5rem]">
      <div className="text-site-neutrals-600 mdL:relative mdL:pl-[4.5rem] lgS:pl-[6.5rem] mx-auto h-full max-w-7xl 2xl:pl-32">
        <div className="lgS:max-w-[33.1875rem] hidden md:block">
          {!hideAuthor && (
            <PostAuthor
              // Remove comment when fix images from wp
              // imageUrl={post.author?.node?.avatar?.url}
              imageUrl={post?.author?.node?.customFields?.authorImage ?? post?.author?.node?.avatar?.url ?? ''}
              imageSize={71}
              name={post?.author?.node?.name ?? ''}
              description={post?.author?.node?.description ?? ''}
              authorSlug={post?.author?.node?.slug ?? ''}
              authorNameClasses="[&_span]:underline text-lg text-site-neutrals-900"
            />
          )}
        </div>
        <div className="mdL:absolute mdL:h-[calc(100%-208px)] mdL:top-0 mdL:left-0">
          <div className="mdL:my-0 mdL:sticky mdL:top-32 my-12 hidden md:my-8 md:flex">
            {!hideSharing && (
              <ShareComponent
                postTitle={post?.title ?? 'BairesDev Blog'}
                postUrl={url}
                classNames="mdL:flex-col mdL:absolute mdL:left-0 mdL:top-0"
              />
            )}
          </div>
        </div>
        {post?.featuredImage && (
          <div className="mdS:mt-14 mdL:mt-8 relative mt-12 aspect-video w-full md:mt-0">
            <Image
              src={post.featuredImage?.node?.mediaDetails?.file ?? ''}
              alt={post.featuredImage?.node?.altText ?? ''}
              fill
              priority
              sizes="(min-width: 1920px) 53rem, (min-width: 1512px) 70rem, (min-width: 1280px) 58rem, (min-width: 1024px) 53rem, (min-width: 834px) 45rem, 100vw"
              className="object-cover"
              loading="eager"
            />
          </div>
        )}
        {hasArticlesContentsLinks && (
          <InputDropdown
            name="Article Contents"
            defaultValue="Article Contents"
            options={articleIndex.map((item) => ({ label: item.title, value: item.id }))}
            isSelect
            onChangeSelect={(opt) => scrollToSection(opt.value)}
            className="mt-8"
            fieldClassName="!p-4 !rounded-lg"
            iconName="ExpandMore"
          />
        )}

        <article className={twMerge('site-paragraph-xl mt-12', styles.root)}>
          {(loaded ? htmlSections : [articleContents]).map((html, index) => (
            <React.Fragment key={html}>
              <span
                key={html}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(html, {
                    ADD_TAGS: ['iframe'],
                    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'],
                  }),
                }}
              />
              {loaded && codeSections[index] && (
                <SyntaxHighlighter language={codeSections[index].language} style={style}>
                  {codeSections[index].code ?? ''}
                </SyntaxHighlighter>
              )}
            </React.Fragment>
          ))}
        </article>

        <div className="my-12 flex md:hidden">
          {!hideSharing && <ShareComponent postTitle={post?.title ?? 'BairesDev Blog'} postUrl={url} />}
        </div>

        {(post?.tags?.nodes?.length ?? 0) > 0 && (
          <div className="mb-10 flex w-full flex-wrap items-end gap-4 md:mt-12">
            <span className="text-site-neutrals-600 site-paragraph-s">Article tags:</span>
            {post?.tags?.nodes?.map((tag) => (
              <div className="site-paragraph-xs" key={tag?.slug}>
                <Chip
                  name={tag?.name ?? ''}
                  link={`/tag/${tag?.slug ?? ''}`}
                  bg="Light"
                  customClasses="!rounded-lg"
                />
              </div>
            ))}
          </div>
        )}

        {!hideAuthor && (
          <div>
            <PostFooterAuthor
              // Remove comment when fix images from wp
              // imageUrl={post.author?.node?.avatar?.url}
              imageUrl={post?.author?.node?.customFields?.authorImage ?? post?.author?.node?.avatar?.url ?? ''}
              name={post?.author?.node?.name ?? ''}
              description={
                extractText(post?.author?.node?.customFields?.bioLong) ?? post?.author?.node?.description
              }
              authorSlug={post?.author?.node?.slug ?? ''}
            />
          </div>
        )}
        <PrevNextArticles previousArticle={previousArticle ?? null} nextArticle={nextArticle ?? null} />
      </div>
    </section>
  );
}

export default PostBody;
